import { Schema, SchemaPerspective } from 'types/schema';
import { FilterExpression } from 'types/filters';
import { evaluateFilterExpression } from '../filterEvaluation';

/**
 * The minimal sphere shape an assignee filter reads: the vertex `id`/`label` and
 * the flattened `properties` bag. Intentionally looser than the full `Sphere`
 * type so callers holding a partial projection (e.g. the assignment route's
 * `SphereInfo`) can validate without a cast.
 */
export interface AssigneeSphereLike {
  id?: string;
  label?: string;
  properties?: Record<string, any>;
}

/**
 * validatePerspectiveAssignments.ts
 * -----------------------------------------------------------------------------
 * Generic enforcement of perspective `assignee_filter` rules at the graph-write
 * boundary.
 *
 * A schema perspective may declare an `assignee_filter` — the set of sphere
 * shapes permitted as that perspective's assignee (e.g. `center_priority`'s
 * `center_parent` allows only `label=center`, `type=center_priority`, or
 * `type=center_pillar`). Historically this was consulted ONLY to auto-infer a
 * default parent/center; an explicitly-supplied assignment (the deploy saga,
 * scripts, service-token requests) was trusted verbatim, so a structurally
 * invalid edge (e.g. a Priority parented under a Strategy) could be written.
 *
 * These helpers evaluate EVERY assignment against its perspective's filter,
 * generically over whatever perspectives a schema defines — nothing is
 * special-cased to `center_parent`/`center_center`. Callers assert validity
 * before persisting so no path can write an edge the schema forbids.
 */

/** A single perspective assignment to validate. */
export interface AssignmentToValidate {
  perspectiveSystemName: string;
  assignedSphere: AssigneeSphereLike;
}

/** One failed assignment: the perspective, the target schema, and what was rejected. */
export interface AssigneeFilterViolation {
  perspectiveSystemName: string;
  targetSchemaSystemName: string;
  assigneeId?: string;
  assigneeType?: string;
  assigneeLabel?: string;
  allowedFilters: FilterExpression[];
}

/**
 * Thrown when one or more perspective assignments violate the target schema's
 * `assignee_filter`. Carries the full violation list for structured logging.
 */
export class AssigneeFilterViolationError extends Error {
  readonly violations: AssigneeFilterViolation[];

  constructor(violations: AssigneeFilterViolation[]) {
    super(AssigneeFilterViolationError.formatMessage(violations));
    this.name = 'AssigneeFilterViolationError';
    this.violations = violations;
  }

  static formatMessage(violations: AssigneeFilterViolation[]): string {
    return violations
      .map(
        (v) =>
          `Perspective '${v.perspectiveSystemName}' on '${v.targetSchemaSystemName}' does not permit an ` +
          `assignee of type='${v.assigneeType ?? 'unknown'}' label='${v.assigneeLabel ?? 'unknown'}'` +
          (v.assigneeId ? ` (id=${v.assigneeId})` : '') +
          `. Allowed: ${JSON.stringify(v.allowedFilters)}`
      )
      .join('; ');
  }
}

/**
 * Evaluate one sphere against a single `assignee_filter` expression using the
 * canonical UnifiedFilters evaluator. The eval context is the sphere's flattened
 * property bag plus `id`/`label` — so `property: 'type'` resolves to
 * `properties.type`, `property: 'label'` to the vertex label, etc. Mirrors the
 * context shape used by the applicability validator.
 */
export function evaluateAssigneeFilter(
  sphere: AssigneeSphereLike,
  filter: FilterExpression
): boolean {
  const evalContext: Record<string, unknown> = {
    // Spreading undefined/null is already a no-op, so the `?? {}` was dead.
    ...(sphere?.properties as Record<string, unknown> | undefined),
    id: sphere?.id,
    label: sphere?.label,
  };
  // Default a missing operator to 'equals' for legacy filters that omitted it.
  const expression = (filter?.operator ? filter : { ...filter, operator: 'equals' }) as FilterExpression;
  try {
    return evaluateFilterExpression(expression, evalContext);
  } catch {
    return false;
  }
}

/**
 * A sphere satisfies a perspective's `assignee_filter` when it matches ANY of the
 * alternatives (OR semantics — one parent can't be `label=center` AND
 * `type=center_pillar` at once). An empty/undefined filter is unconstrained:
 * any assignee is allowed.
 */
export function matchesAssigneeFilter(
  sphere: AssigneeSphereLike,
  assigneeFilter: FilterExpression[] | undefined | null
): boolean {
  if (!assigneeFilter || assigneeFilter.length === 0) return true;
  return assigneeFilter.some((filter) => evaluateAssigneeFilter(sphere, filter));
}

function normalizeType(t: unknown): string | undefined {
  return Array.isArray(t) ? (t[0] as string | undefined) : (t as string | undefined);
}

/**
 * Validate every perspective assignment against its perspective's
 * `assignee_filter` on the target schema. Generic over ALL perspectives the
 * schema defines — none is special-cased.
 *
 * Skipped (impose no constraint): an assignment whose perspective is not defined
 * on the schema, a perspective with no `assignee_filter`, and an assignment with
 * no assignee sphere.
 *
 * @returns the list of violations (empty when every assignment is valid).
 */
export function findAssigneeFilterViolations(
  schema: Schema,
  assignments: AssignmentToValidate[]
): AssigneeFilterViolation[] {
  const violations: AssigneeFilterViolation[] = [];

  const perspectivesByName = new Map<string, SchemaPerspective>();
  for (const p of schema.perspectives ?? []) {
    perspectivesByName.set(p.system_name, p);
  }

  for (const assignment of assignments) {
    const perspective = perspectivesByName.get(assignment.perspectiveSystemName);
    if (!perspective) continue; // not a schema-defined perspective → nothing to constrain
    const filter = perspective.assignee_filter;
    if (!filter || filter.length === 0) continue; // unconstrained perspective
    if (!assignment.assignedSphere) continue;

    if (!matchesAssigneeFilter(assignment.assignedSphere, filter)) {
      violations.push({
        perspectiveSystemName: assignment.perspectiveSystemName,
        targetSchemaSystemName: schema.system_name,
        assigneeId: assignment.assignedSphere.id,
        assigneeType: normalizeType(assignment.assignedSphere.properties?.['type']),
        assigneeLabel: assignment.assignedSphere.label,
        allowedFilters: filter,
      });
    }
  }

  return violations;
}

/**
 * Assert that every perspective assignment satisfies its perspective's
 * `assignee_filter`, throwing {@link AssigneeFilterViolationError} listing all
 * violations if not.
 *
 * This is the hard graph-write invariant: a perspective edge must point at an
 * assignee the schema permits. Enforced for EVERY assignment — explicit
 * (caller-supplied) and auto-inferred alike — so no code path (deploy saga,
 * scripts, service-token requests) can persist a structurally invalid edge.
 */
export function assertPerspectiveAssignmentsValid(
  schema: Schema,
  assignments: AssignmentToValidate[]
): void {
  const violations = findAssigneeFilterViolations(schema, assignments);
  if (violations.length > 0) {
    throw new AssigneeFilterViolationError(violations);
  }
}
